import { useEffect, useState } from "react";
import api from "@/lib/api";
import TagBadge from "@/components/TagBadge";
import {
  displayName,
  initials,
  formatDate,
  formatRelative,
} from "@/lib/utils-format";
import {
  X,
  TelegramLogo,
  ArrowClockwise,
  Plus,
  Calendar,
  CursorClick,
  PaperPlaneTilt,
  Tag as TagIcon,
} from "@phosphor-icons/react";

export default function CustomerDrawer({ userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tags");
  const [allTags, setAllTags] = useState([]);
  const [showAddTag, setShowAddTag] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/subscribers/${userId}`);
    setData(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    api.get("/tags").then((r) => setAllTags(r.data));
  }, [userId]);

  const recheckChannel = async () => {
    await api.post(`/subscribers/${userId}/check-channel`);
    await load();
    onChanged?.();
  };

  const assignTag = async (tagId) => {
    await api.post(`/subscribers/${userId}/tags`, { tag_id: tagId });
    await load();
    setShowAddTag(false);
    onChanged?.();
  };

  const removeTag = async (tag) => {
    await api.delete(`/subscribers/${userId}/tags/${tag.id}`);
    await load();
    onChanged?.();
  };

  const u = data?.user || {};

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="customer-drawer"
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        data-testid="drawer-overlay"
      />
      <div className="relative w-full max-w-2xl glass border-l border-white/10 h-screen overflow-y-auto fade-up">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            Карточка подписчика
          </div>
          <button
            data-testid="close-drawer-btn"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-100"
          >
            <X size={18} />
          </button>
        </div>

        {loading || !data ? (
          <div className="p-8 text-zinc-500 text-sm">Загрузка карточки…</div>
        ) : (
          <>
            {/* Profile */}
            <div className="px-6 py-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center text-xl font-bold border border-blue-500/30">
                  {initials(u)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-semibold tracking-tight">
                    {displayName(u)}
                  </h2>
                  {u.username && (
                    <a
                      href={`https://t.me/${u.username}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-[#2AABEE] hover:underline font-mono"
                    >
                      <TelegramLogo size={12} weight="fill" /> @{u.username}
                    </a>
                  )}
                  <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500 font-mono">
                    <div>ID: {u.user_id}</div>
                    {u.language_code && <div>lang: {u.language_code}</div>}
                    {u.first_seen && (
                      <div>с {formatDate(u.first_seen)}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                <InfoBox
                  label="Канал"
                  value={
                    u.main_channel_status === "subscribed"
                      ? "подписан"
                      : u.main_channel_status === "not_subscribed"
                      ? "не подписан"
                      : "не проверено"
                  }
                  accent={
                    u.main_channel_status === "subscribed"
                      ? "#2AABEE"
                      : u.main_channel_status === "not_subscribed"
                      ? "#ef4444"
                      : "#71717a"
                  }
                  action={
                    <button
                      onClick={recheckChannel}
                      data-testid="recheck-channel-btn"
                      className="text-zinc-500 hover:text-zinc-100"
                      title="Перепроверить"
                    >
                      <ArrowClockwise size={12} weight="bold" />
                    </button>
                  }
                />
                <InfoBox
                  label="Статус бота"
                  value={u.is_bot_blocked ? "заблокирован" : "активен"}
                  accent={u.is_bot_blocked ? "#ef4444" : "#10b981"}
                />
                <InfoBox label="UTM source" value={u.utm_source || "—"} />
                <InfoBox label="UTM campaign" value={u.utm_campaign || "—"} />
                <InfoBox
                  label="Последняя активность"
                  value={formatRelative(u.last_activity)}
                />
                <InfoBox
                  label="Регистраций"
                  value={String(data.registrations.length)}
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-zinc-800 px-6 flex gap-1">
              {[
                { id: "tags", label: "Теги", icon: TagIcon, count: data.tags.length },
                { id: "history", label: "История", icon: Calendar, count: data.registrations.length + data.incomplete.length },
                { id: "clicks", label: "Клики", icon: CursorClick, count: data.clicks.length },
                { id: "comms", label: "Рассылки", icon: PaperPlaneTilt, count: data.broadcasts.length },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-3 text-sm flex items-center gap-1.5 border-b-2 transition-colors ${
                    tab === t.id
                      ? "border-blue-500 text-blue-400"
                      : "border-transparent text-zinc-500 hover:text-zinc-200"
                  }`}
                >
                  <t.icon size={14} weight="bold" />
                  {t.label}
                  <span className="text-[10px] font-mono opacity-60">
                    {t.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="px-6 py-5">
              {tab === "tags" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                      Назначенные теги
                    </div>
                    <button
                      data-testid="add-tag-btn"
                      onClick={() => setShowAddTag((s) => !s)}
                      className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-zinc-800 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
                    >
                      <Plus size={12} weight="bold" /> Добавить
                    </button>
                  </div>
                  {showAddTag && (
                    <div className="mb-4 p-3 bg-zinc-900 border border-zinc-800 rounded-md">
                      <div className="text-xs text-zinc-400 mb-2">
                        Выберите тег:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {allTags
                          .filter(
                            (t) => !data.tags.find((ut) => ut.id === t.id)
                          )
                          .map((t) => (
                            <button
                              key={t.id}
                              onClick={() => assignTag(t.id)}
                              data-testid={`assign-tag-${t.id}`}
                            >
                              <TagBadge tag={t} />
                            </button>
                          ))}
                        {allTags.length === 0 && (
                          <div className="text-xs text-zinc-500">
                            Нет доступных тегов
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {data.tags.length === 0 && (
                      <div className="text-sm text-zinc-500">
                        Тегов ещё нет. Они будут проставляться автоматически.
                      </div>
                    )}
                    {data.tags.map((t) => (
                      <TagBadge key={t.id} tag={t} onRemove={removeTag} size="md" />
                    ))}
                  </div>
                </div>
              )}

              {tab === "history" && (
                <div className="space-y-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                      Регистрации
                    </div>
                    {data.registrations.length === 0 ? (
                      <div className="text-sm text-zinc-500">Нет регистраций</div>
                    ) : (
                      <div className="space-y-1.5">
                        {data.registrations.map((r) => (
                          <div
                            key={r.event_id + (r.reg_date || "")}
                            className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2 text-sm"
                          >
                            <div>
                              <div className="font-medium">
                                {r.btn_name || r.full_name || `event#${r.event_id}`}
                              </div>
                              {r.email && (
                                <div className="text-xs text-zinc-500 font-mono">
                                  {r.email}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-zinc-500 font-mono">
                              {formatDate(r.reg_date)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {data.incomplete.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                        Незавершённые
                      </div>
                      <div className="space-y-1.5">
                        {data.incomplete.map((r) => (
                          <div
                            key={r.event_id + r.start_date}
                            className="flex items-center justify-between bg-zinc-900/40 border border-blue-500/20 rounded-md px-3 py-2 text-sm"
                          >
                            <div className="font-medium">
                              {r.btn_name || `event#${r.event_id}`}
                            </div>
                            <div className="text-xs text-zinc-500 font-mono">
                              {formatDate(r.start_date)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === "clicks" && (
                <div className="space-y-1.5">
                  {data.clicks.length === 0 ? (
                    <div className="text-sm text-zinc-500">Нет кликов</div>
                  ) : (
                    data.clicks.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2 text-sm"
                      >
                        <div>
                          <div className="font-mono text-xs text-zinc-400">
                            {c.target_type}
                            {c.target_id ? `#${c.target_id}` : ""}
                          </div>
                          {c.meta && (
                            <div className="text-xs text-zinc-500">
                              {(() => {
                                try {
                                  const m = JSON.parse(c.meta);
                                  return m.name || JSON.stringify(m);
                                } catch {
                                  return c.meta;
                                }
                              })()}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 font-mono">
                          {formatRelative(c.clicked_at)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {tab === "comms" && (
                <div className="space-y-1.5">
                  {data.broadcasts.length === 0 ? (
                    <div className="text-sm text-zinc-500">
                      Этому подписчику ещё не отправлялись рассылки
                    </div>
                  ) : (
                    data.broadcasts.map((b) => (
                      <div
                        key={b.id}
                        className="bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2.5"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs">
                            <BroadcastStatusBadge status={b.status} />
                            <span className="font-mono text-zinc-500">
                              {formatDate(b.sent_at)}
                            </span>
                          </div>
                          {b.send_time && (
                            <span className="text-[10px] text-zinc-500 font-mono">
                              {b.send_time}
                            </span>
                          )}
                        </div>
                        {b.text && (
                          <div className="mt-1.5 text-sm text-zinc-300 line-clamp-2">
                            {b.text.replace(/<[^>]+>/g, "")}
                          </div>
                        )}
                        {b.error && (
                          <div className="mt-1 text-xs text-red-400 font-mono">
                            {b.error}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InfoBox({ label, value, accent = "#a1a1aa", action }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2.5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          {label}
        </div>
        {action}
      </div>
      <div
        className="mt-1 font-mono text-sm truncate"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function BroadcastStatusBadge({ status }) {
  const colors = {
    sent: "#10b981",
    failed: "#ef4444",
    blocked: "#3b82f6",
  };
  const labels = { sent: "доставлено", failed: "ошибка", blocked: "блок" };
  return (
    <span
      className="badge-dot text-[10px] font-mono uppercase tracking-wide"
      style={{ color: colors[status] || "#a1a1aa" }}
    >
      {labels[status] || status}
    </span>
  );
}
